import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ProjectApi } from "~/functions/api/project";
import type { RootState } from "~/store";
import { setCurrentProject, setProjectMembers } from "./auth";

// UI表示用に日本語化されたロール名を含む型 (共通)
interface AddedMemberDisplayInfo {
  id: string;
  displayName: string;
  role: string; // 日本語化されたロール
}

interface DocumentNodeStatus {
  id: string;
  status: boolean;
}

interface ProjectState {
  members: AddedMemberDisplayInfo[];
  documentStatus: DocumentNodeStatus[];
  isLoading: boolean;
  error: string | null;
  currentSession: {
    threadId: string;
    resourceId: string;
  };
}

const initialState: ProjectState = {
  members: [],
  documentStatus: [],
  isLoading: false,
  error: null,
  currentSession: {
    threadId: "",
    resourceId: "",
  },
};

// threadIdとresourceIdを初期化するアクション
export const initializeSessions = createAsyncThunk(
  "project/initializeSessions",
  async (documentId: string, { getState }) => {
    const state = getState() as RootState;
    const { currentUser, currentProject } = state.auth;
    const currentTime = Date.now();

    const agentThreadId = `${currentProject.id}${documentId}${currentUser.id}_${currentTime}`;
    const agentResourceId = `${currentProject.id}${documentId}${currentUser.id}`;

    return {
      currentSession: {
        threadId: agentThreadId,
        resourceId: agentResourceId,
      },
    };
  },
);

// エージェントセッションを開始（または更新）
export const resetModeSession = createAsyncThunk(
  "project/resetModeSession",
  async (documentId: string, { getState }) => {
    const state = getState() as RootState;
    const { currentUser, currentProject } = state.auth;
    const currentTime = Date.now();

    return {
      currentSession: {
        threadId: `${currentProject.id}${documentId}${currentUser.id}_${currentTime}`,
        resourceId: `${currentProject.id}${documentId}${currentUser.id}`,
      },
    };
  },
);

export const fetchProjectData = createAsyncThunk(
  "project/fetchProjectData",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;

      if (!currentProject?.id || !currentUser?.organization_id) {
        return rejectWithValue("プロジェクトまたは組織の情報が不足しています");
      }

      const projectApi = new ProjectApi();
      const [projectResponse, membersResponse] = await Promise.allSettled([
        projectApi.get(currentUser.organization_id, currentProject.id),
        projectApi.getMembers(currentProject.id, currentUser.organization_id),
      ]);

      if (projectResponse.status === "rejected" || membersResponse.status === "rejected") {
        const errors = [];
        if (projectResponse.status === "rejected")
          errors.push(`Project: ${projectResponse.reason}`);
        if (membersResponse.status === "rejected")
          errors.push(`Members: ${membersResponse.reason}`);
        throw new Error(`API calls failed: ${errors.join(", ")}`);
      }

      const project = projectResponse.value;
      const projectMembers = membersResponse.value;

      if (project === null || projectMembers === null) {
        throw new Error("再ログインしてください");
      }

      // 競合防止: 直近に切替が行われており、現在のcurrentProject.idと取得したproject.idが異なる場合は
      // 古いプロジェクトで上書きしない
      const latestId = (getState() as RootState).auth.currentProject?.id;
      if (latestId === project.id) {
        dispatch(setCurrentProject(project));
      }

      if (!Array.isArray(projectMembers)) {
        throw new Error("プロジェクトメンバーのデータが無効です");
      }

      const members = projectMembers
        .filter((member) => member?.user?.id && member?.user?.display_name && member?.role)
        .map((member) => ({
          id: member.user.id,
          displayName: member.user.display_name,
          role: member.role,
        }));

      const documentStatus: DocumentNodeStatus[] = [
        {
          id: "app_design",
          status: Boolean(project.outputs?.["app-design"]?.id),
        },
        {
          id: "entity_relationship",
          status: Boolean(project.outputs?.["entity-relationship"]?.id),
        },
        {
          id: "functional_requirement",
          status: Boolean(project.outputs?.["functional-requirement"]?.id),
        },
        {
          id: "field_design",
          status: Boolean(project.outputs?.["field-design"]?.id),
        },
        {
          id: "non_functional_requirement",
          status: Boolean(project.outputs?.["non-functional-requirement"]?.id),
        },
        {
          id: "plugin_proposal",
          status: Boolean(project.outputs?.["plugin-proposal"]?.id),
        },
        {
          id: "system_architecture",
          status: Boolean(project.outputs?.["system-architecture"]?.id),
        },
        {
          id: "system_proposal",
          status: Boolean(project.outputs?.["system-proposal"]?.id),
        },
        {
          id: "work_flow",
          status: Boolean(project.outputs?.["workflow"]?.id),
        },
        {
          id: "customize_specification",
          status: Boolean(project.outputs?.["customize-specification"]?.id),
        },
      ];

      dispatch(setProjectMembers(projectMembers));

      // モード別セッションも現在のプロジェクト・ユーザーに基づいて再初期化
      const currentTime = Date.now();
      const askThreadId = `${project.id}${state.auth.currentUser.id}agent${currentTime}`;
      const askResourceId = `${project.id}${state.auth.currentUser.id}agent`;

      return {
        members,
        documentStatus,
        currentSession: {
          mode: "agent" as const,
          threadId: askThreadId,
          resourceId: askResourceId,
        },
      };
    } catch (error) {
      console.error("fetchProjectData error:", error);
      return rejectWithValue(error instanceof Error ? error.message : "エラーが発生しました");
    }
  },
);

const projectSlice = createSlice({
  name: "project",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateMember: (state, action) => {
      const { userId, newRole } = action.payload;
      // newRole は projectTopListContent で mapRoleToJapanese された値が渡ってくる想定
      state.members = state.members.map((member) =>
        member.id === userId ? { ...member, role: newRole } : member,
      );
    },
    removeMember: (state, action) => {
      const userId = action.payload;
      state.members = state.members.filter((member) => member.id !== userId);
    },
    addMembers: (state, action) => {
      // 追加されたメンバーの情報を既存のリストに結合
      // 重複を避ける場合は、ここや InviteProjectModal 側で既存メンバーかどうかのチェックが必要
      state.members = [...state.members, ...action.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjectData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProjectData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.members = action.payload.members;
        state.documentStatus = action.payload.documentStatus;
      })
      .addCase(fetchProjectData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // セッションID初期化
      .addCase(initializeSessions.fulfilled, (state, action) => {
        const payload: any = action.payload || {};
        if (payload.currentSession) {
          state.currentSession = payload.currentSession;
        }
      })
      // モード別セッション更新
      .addCase(resetModeSession.fulfilled, (state, action) => {
        const payload: any = action.payload || {};
        if (payload.currentSession) {
          state.currentSession = payload.currentSession;
        }
      });
  },
});

export const { clearError, updateMember, removeMember, addMembers } = projectSlice.actions;

export default projectSlice.reducer;
